import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { PlatformAuthProvider } from "@/context/PlatformAuthContext";
import PlatformLogin from "@/pages/PlatformLogin";
import Platform from "@/pages/Platform";
import { BrandingProvider } from "@/context/BrandingContext";
import { I18nProvider } from "@/context/I18nContext";
import { Toaster } from "@/components/ui/sonner";
import { Loading } from "@/components/common/Primitives";
import { AppShell } from "@/components/layout/AppShell";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Customers from "@/pages/Customers";
import CustomerDetail from "@/pages/CustomerDetail";
import Contracts from "@/pages/Contracts";
import ContractDetail from "@/pages/ContractDetail";
import Documents from "@/pages/Documents";
import Renewals from "@/pages/Renewals";
import Tasks from "@/pages/Tasks";
import Communication from "@/pages/Communication";
import Sales from "@/pages/Sales";
import Reports from "@/pages/Reports";
import AIAssistant from "@/pages/AIAssistant";
import Integrations from "@/pages/Integrations";
import Settings from "@/pages/Settings";
import WawiDashboard from "@/pages/WawiDashboard";
import WawiArticles from "@/pages/WawiArticles";
import WawiCategories from "@/pages/WawiCategories";
import WawiStock from "@/pages/WawiStock";
import WawiTransfers from "@/pages/WawiTransfers";
import WawiInventur from "@/pages/WawiInventur";
import WawiDevices from "@/pages/WawiDevices";
import WawiReceiptScan from "@/pages/WawiReceiptScan";
import MfOverview from "@/pages/mobilfunk/MfOverview";
import MfProviders from "@/pages/mobilfunk/MfProviders";
import MfTariffs from "@/pages/mobilfunk/MfTariffs";
import MfPromotions from "@/pages/mobilfunk/MfPromotions";
import MfCommissionRules from "@/pages/mobilfunk/MfCommissionRules";
import MfSettings from "@/pages/mobilfunk/MfSettings";
import MfComingSoon from "@/pages/mobilfunk/MfComingSoon";
import MfImport from "@/pages/mobilfunk/MfImport";
import MfContracts from "@/pages/mobilfunk/MfContracts";
import MfCommissionCalc from "@/pages/mobilfunk/MfCommissionCalc";
import MfSettlements from "@/pages/mobilfunk/MfSettlements";
import MfReconcile from "@/pages/mobilfunk/MfReconcile";
import MfClaims from "@/pages/mobilfunk/MfClaims";
import MfReports from "@/pages/mobilfunk/MfReports";

function Protected({ children }) {
  const { status } = useAuth();
  if (status === "checking") return <div className="flex min-h-screen items-center justify-center"><Loading /></div>;
  if (status === "guest") return <Navigate to="/login" replace />;
  return children;
}

function Public({ children }) {
  const { status } = useAuth();
  if (status === "checking") return <div className="flex min-h-screen items-center justify-center"><Loading /></div>;
  if (status === "authed") return <Navigate to="/" replace />;
  return children;
}

function Shell() {
  return (
    <BrandingProvider>
      <AppShell />
    </BrandingProvider>
  );
}

function App() {
  return (
    <I18nProvider>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<Public><BrandingProvider><Login /></BrandingProvider></Public>} />
            <Route path="/platform/login" element={<PlatformAuthProvider><PlatformLogin /></PlatformAuthProvider>} />
            <Route path="/platform" element={<PlatformAuthProvider><Platform /></PlatformAuthProvider>} />
            <Route element={<Protected><Shell /></Protected>}>
              <Route path="/" element={<Dashboard />} />
              <Route path="/kunden" element={<Customers />} />
              <Route path="/kunden/:id" element={<CustomerDetail />} />
              <Route path="/vertraege" element={<Contracts />} />
              <Route path="/vertraege/:id" element={<ContractDetail />} />
              <Route path="/dokumente" element={<Documents />} />
              <Route path="/verlaengerungen" element={<Renewals />} />
              <Route path="/aufgaben" element={<Tasks />} />
              <Route path="/kommunikation" element={<Communication />} />
              <Route path="/vertrieb" element={<Sales />} />
              <Route path="/wawi" element={<WawiDashboard />} />
              <Route path="/wawi/artikel" element={<WawiArticles />} />
              <Route path="/wawi/kategorien" element={<WawiCategories />} />
              <Route path="/wawi/lager" element={<WawiStock />} />
              <Route path="/wawi/umlagerungen" element={<WawiTransfers />} />
              <Route path="/wawi/inventur" element={<WawiInventur />} />
              <Route path="/wawi/wareneingang-scan" element={<WawiReceiptScan />} />
              <Route path="/wawi/geraete" element={<WawiDevices />} />
              <Route path="/mobilfunk" element={<MfOverview />} />
              <Route path="/mobilfunk/anbieter" element={<MfProviders />} />
              <Route path="/mobilfunk/tarife" element={<MfTariffs />} />
              <Route path="/mobilfunk/aktionen" element={<MfPromotions />} />
              <Route path="/mobilfunk/provisionsmodelle" element={<MfCommissionRules />} />
              <Route path="/mobilfunk/import" element={<MfImport />} />
              <Route path="/mobilfunk/abschluesse" element={<MfContracts />} />
              <Route path="/mobilfunk/provisionsberechnung" element={<MfCommissionCalc />} />
              <Route path="/mobilfunk/abrechnungen" element={<MfSettlements />} />
              <Route path="/mobilfunk/soll-ist" element={<MfReconcile />} />
              <Route path="/mobilfunk/reklamationen" element={<MfClaims />} />
              <Route path="/mobilfunk/auswertungen" element={<MfReports />} />
              <Route path="/mobilfunk/einstellungen" element={<MfSettings />} />
              <Route path="/berichte" element={<Reports />} />
              <Route path="/ki-assistent" element={<AIAssistant />} />
              <Route path="/integrationen" element={<Integrations />} />
              <Route path="/einstellungen" element={<Settings />} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </I18nProvider>
  );
}

export default App;
